import type { GreenroomUser, Meeting } from '../stores/types/session';
import { CreateNotification as DatabaseCreateNotification } from '../connection/home';
import { ConnectedUser } from '../stores/types/connection';

import store from '../stores/main';

function getGreenroomUser(user: any): any {
	return user.gr_user;
}

const getWallpaper = () => {
	const {
		ShotsReducer: {
			shots,
		},
	} = store.getState();

	return shots[0] ? shots[0].shot_style.applied.wallpaperImage : undefined;
};

const baseNotification = (meeting: any, user: GreenroomUser) => {
	return {
		meeting: meeting.meeting,
		sending_user: user.gr_user,
		channel: meeting.channel,
		created: new Date(),
		type: '',
		receiving_emails: [],
		receiving_users: [],
		data: {
			message: '',
			meeting_slug: meeting.meeting_id,
			meeting_name: meeting.title,
			first_name: user.first_name,
			last_name: user.last_name,
		},
	};
};

export const commentBasicNotification = (token: string, user: GreenroomUser, sent_to_users: ConnectedUser[], meeting: any) => {
	if (!token || !user || !user.gr_user) return;

	const uniqueUsers = new Set(sent_to_users?.map(getGreenroomUser));
	if (uniqueUsers.size === 0) return;

	const receiving_users = Array.from(uniqueUsers).filter(Boolean).filter((userID: number) => userID !== user?.gr_user);
	if (receiving_users.length < 1) return;

	const base = baseNotification(meeting, user);

	const notification = {
		...base,
		receiving_users: receiving_users,
		type: 'comment-basic',
		data: {
			...base.data,
			message: '**%%notification_sender%%** sent you a message in **%%notification_meeting%%**',
			picture: user.avatar,
		},
	};

	DatabaseCreateNotification(token, notification);
};

export const commentMentionNotification = (token: string, user: GreenroomUser, sent_to_users: ConnectedUser[], meeting: any) => {
	if (!token || !user || !user.gr_user) return;

	const uniqueUsers = new Set(sent_to_users?.map(getGreenroomUser));
	if (uniqueUsers.size === 0) return;

	const receiving_users = Array.from(uniqueUsers).filter(Boolean).filter((userID: number) => userID !== user?.gr_user);
	if (receiving_users.length < 1) return;

	const base = baseNotification(meeting, user);

	const notification = {
		...base,
		receiving_users: receiving_users,
		type: 'comment-mention',
		data: {
			...base.data,
			message: '**%%notification_sender%%** mentioned you in a comment on **%%notification_meeting%%**',
			picture: user.avatar,
		},
	};

	DatabaseCreateNotification(token, notification);
};

export const inviationPresenterToGreenroomNotification = (token: string, user: GreenroomUser, sent_to_emails: string[], meeting: any) => {
	if (!token || !user || !user.gr_user) return;

	const base = baseNotification(meeting, user);

	const notification = {
		...base,
		receiving_emails: sent_to_emails,
		type: 'invite-presenter',
		data: {
			...base.data,
			message: '**%%notification_sender%%** invited you as a presenter on **%%notification_meeting%%**',
			wallpaper: getWallpaper(),
		},
	};

	DatabaseCreateNotification(token, notification);
};

export const inviationViewerToGreenroomNotification = (token: string, user: GreenroomUser, sent_to_emails: string[], meeting: any) => {
	if (!token || !user || !user.gr_user) return;

	const base = baseNotification(meeting, user);

	const notification = {
		...base,
		receiving_emails: sent_to_emails,
		type: 'invite-viewer',
		data: {
			...base.data,
			message: '**%%notification_sender%%** invited you as a viewer on **%%notification_meeting%%**',
			wallpaper: getWallpaper(),
		},
	};

	DatabaseCreateNotification(token, notification);
};

export const showSessionCreatedNotification = (token: string, user: GreenroomUser, sent_to_emails: string[], meeting: any, start_time: Date, show_session_type: string) => {
	if (!token || !user || !user.gr_user) return;

	const rehearsalStartTime = new Date(start_time);
	const timestamp = rehearsalStartTime.valueOf();

	const base = baseNotification(meeting, user);

	const notification = {
		...base,
		receiving_emails: sent_to_emails,
		type: 'rehearsal-create',
		data: {
			...base.data,
			message: `**%%notification_meeting%%** event ${show_session_type} was added for **%%notification_timestamp_data%%**`,
			timestamp_data: timestamp,
			wallpaper: getWallpaper(),
		},
	};

	DatabaseCreateNotification(token, notification);
};

export interface CreateNotificationOptions {
	token: string,
	user: GreenroomUser,
	receiving_emails?: string[],
	receiving_users?: number[],
	type: string,
	meeting: Meeting,
	message: string,
	other_data?: Object,
};

export const CreateNotification = (options: CreateNotificationOptions) => {
	if (!options.user.gr_user) return;

	const base = baseNotification(options.meeting, options.user);

	const notification = {
		...base,
		receiving_emails: options.receiving_emails || [],
		receiving_users: options.receiving_users || [],
		type: options.type,
		data: {
			...base.data,
			message: options.message,
			...options.other_data,
		},
	};

	DatabaseCreateNotification(options.token, notification);
};